import {
  BookmarkSimple,
  ChatCircle,
  DotsThreeVertical,
  Heart,
  MapPin,
} from '@phosphor-icons/react';
import type { Post } from '@sirocco/shared';
import { Link } from 'react-router-dom';
import { events as eventsApi } from '../api/endpoints';
import { Avatar } from './ui/Avatar';
import { EventCard } from './EventCard';
import { useAsync } from '../hooks/useAsync';
import { timeAgo } from '../utils/format';
import { PostText } from './ui/PostText';
import { useSession } from '../store/SessionContext';
import { profilePath } from '../utils/routes';
import { t } from '../i18n';

/**
 * Un post du feed.
 *
 * Les compteurs sont mis a jour de facon optimiste par l'ecran parent :
 * le like doit repondre au doigt, pas au reseau. Le coeur et le signet
 * passent en `fill` une fois actifs, comme le prevoit le systeme
 * d'icones ; le coeur est la seule couleur chaude autorisee dans le feed.
 */
export function PostCard({
  post,
  compact = false,
  onLike,
  onBookmark,
  onMenu,
  onOpenComments,
  onOpenPhoto,
  sansAsso = false,
}: {
  post: Post;
  /**
   * Le meme post, en plus petit et sans prise.
   *
   * Pour la bulle d'une conversation. Liker et enregistrer depuis un fil
   * de messages serait un geste de trop au mauvais endroit : les
   * compteurs restent, mais comme des faits — on va sur le post pour agir
   * dessus. Le menu disparait : ses actions sont celles du feed, et le
   * message, lui, a deja les siennes par l'appui long.
   */
  compact?: boolean;
  onLike?: (post: Post) => void;
  onBookmark?: (post: Post) => void;
  onMenu?: (post: Post, trigger: HTMLElement) => void;
  /** `compose` a vrai quand on vient du bouton : le clavier suit. */
  onOpenComments?: (post: Post, compose?: boolean) => void;
  /** Ouvre la photo en plein ecran. */
  onOpenPhoto?: (src: string) => void;
  /** Sur la page de son association, son nom a cote de l'auteur ne dirait rien de plus. */
  sansAsso?: boolean;
}) {
  /**
   * Tout le corps du post ouvre son fil de commentaires.
   * Les liens vers le profil, le menu, les compteurs et la photo ont leur
   * propre geste : ils arretent la propagation plutot que de sortir du
   * bloc cliquable, ce qui garderait des trous dans la zone d'appui.
   */
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();

  // Mon propre pseudo mene a mon onglet Profil, pas a la fiche d'un autre.
  const { user, server } = useSession();
  const authorPath = profilePath(post.author.id, user?.id);

  return (
    <article
      className={`post${compact ? ' post--compact' : ''}`}
      /* Le repere du temps de lecture : voir `pourtoi/lecture`. */
      data-post={post.id}
      onClick={() => onOpenComments?.(post)}
    >
      <Link
        to={authorPath}
        className="post__avatar"
        aria-label={post.author.username}
        onClick={stop}
      >
        <Avatar user={post.author} size={compact ? 32 : 38} />
      </Link>

      <div className="post__body">
        <header className="post__head">
          <Link to={authorPath} className="post__author" onClick={stop}>
            {post.author.username}
          </Link>
          {/* Publie dans le fil d'une association : son nom mene a sa page. */}
          {!sansAsso && post.asso && server?.associations?.[post.asso] && (
            <Link to={`/app/annuaire/associations/${post.asso}`} className="post__asso" onClick={stop}>
              · {server.associations[post.asso].nom}
            </Link>
          )}
          <span className="post__time">· {timeAgo(post.createdAt)}</span>
          {onMenu && (
            <button
              className="post__menu"
              aria-label={t.fil.commun.actionsPost}
              /* Le bouton part avec le post : le menu sort de lui, et il y
                 en a un par carte — aucune ref ne pourrait les tenir tous. */
              onClick={(event) => {
                stop(event);
                onMenu(post, event.currentTarget);
              }}
            >
              <DotsThreeVertical size={20} weight="bold" />
            </button>
          )}
        </header>

        {post.content && <PostText content={post.content} mentions={post.mentions} />}

        {/*
          L'evenement joint garde son propre geste : il mene a sa fiche,
          pas au fil de commentaires du post.
        */}
        {post.attachedEventId && <AttachedEvent eventId={post.attachedEventId} onNavigate={stop} />}

        {post.imageUrl &&
          (onOpenPhoto ? (
            <button
              className="post__photo"
              aria-label={t.fil.commun.voirPhoto}
              onClick={(event) => {
                stop(event);
                onOpenPhoto(post.imageUrl!);
              }}
            >
              <img src={post.imageUrl} alt={t.fil.commun.photoDe(post.author.username)} />
            </button>
          ) : (
            /* Sans plein ecran a offrir, l'image n'est plus un bouton :
               un bouton qui ne fait rien se laisse appuyer pour rien. */
            <span className="post__photo">
              <img src={post.imageUrl} alt={t.fil.commun.photoDe(post.author.username)} />
            </span>
          ))}

        {compact ? (
          /*
            Les memes reperes, mais lus et non touches.

            Un coeur qui ne bat pas sous le doigt serait pire qu'un coeur
            absent ; en span, il n'annonce rien qu'il ne tienne — il dit
            combien de gens ont aime, comme le nombre de reponses a cote.
            Le signet, lui, a desormais son compteur : il ne se contente
            plus de dire « tu l'as garde », il dit combien de gens l'ont
            garde. Il parait donc toujours, comme les deux autres, plein
            quand c'est moi qui l'ai range.

            Les deux autres suivent la meme regle depuis : un zero ne dit
            rien qu'une bulle vide ne dise deja, et il en disait meme
            moins — quatre rangees de « 0 0 » a la suite donnaient un fil
            eteint, la ou des signes seuls laissent la place au contenu.
          */
          <footer className="post__actions">
            <span className="post__action">
              <ChatCircle size={17} />
              {post.commentCount > 0 && <span>{post.commentCount}</span>}
            </span>
            <span className={`post__action${post.liked ? ' post__action--liked' : ''}`}>
              <Heart size={17} weight={post.liked ? 'fill' : 'regular'} />
              {post.likeCount > 0 && <span>{post.likeCount}</span>}
            </span>
            <span className={`post__action${post.bookmarked ? ' post__action--saved' : ''}`}>
              <BookmarkSimple size={17} weight={post.bookmarked ? 'fill' : 'regular'} />
              {post.bookmarkCount > 0 && <span>{post.bookmarkCount}</span>}
            </span>
            {post.location && (
              <span className="post__place">
                <MapPin size={15} weight="fill" />
                {post.location}
              </span>
            )}
          </footer>
        ) : (
          <footer className="post__actions">
            {/*
              Le compteur mene au meme fil que le corps du post, mais pret a
              ecrire : toucher la bulle, c'est vouloir repondre, pas lire.
            */}
            <button
              className="post__action"
              onClick={(event) => {
                stop(event);
                onOpenComments?.(post, true);
              }}
              /* « 0 commentaires » se lit mal a voix haute : sans
                 reponse, le bouton dit ce qu'il fait. */
              aria-label={
                post.commentCount > 0 ? `${post.commentCount} commentaires` : t.fil.commun.commenter
              }
            >
              <ChatCircle size={17} />
              {post.commentCount > 0 && <span>{post.commentCount}</span>}
            </button>

            <button
              className={`post__action${post.liked ? ' post__action--liked' : ''}`}
              onClick={(event) => {
                stop(event);
                onLike?.(post);
              }}
              aria-label={post.liked ? t.fil.commun.retirerLike : t.fil.commun.liker}
              aria-pressed={post.liked}
            >
              <Heart size={17} weight={post.liked ? 'fill' : 'regular'} />
              {post.likeCount > 0 && <span>{post.likeCount}</span>}
            </button>

            <button
              className={`post__action${post.bookmarked ? ' post__action--saved' : ''}`}
              onClick={(event) => {
                stop(event);
                onBookmark?.(post);
              }}
              aria-label={post.bookmarked ? t.fil.commun.retirerSignet : t.fil.commun.enregistrer}
              aria-pressed={post.bookmarked}
            >
              <BookmarkSimple size={17} weight={post.bookmarked ? 'fill' : 'regular'} />
              {post.bookmarkCount > 0 && <span>{post.bookmarkCount}</span>}
            </button>

            {/*
              Le lieu, au bout de la rangee. Il etait pose sous le texte,
              entre le post et ses boutons, ou il coupait la lecture pour
              une information qui n'est qu'un complement. Il est mieux ici :
              avec les autres details du post, et sans rien interrompre.
            */}
            {post.location && (
              <span className="post__place">
                <MapPin size={15} weight="fill" />
                {post.location}
              </span>
            )}
          </footer>
        )}
      </div>
    </article>
  );
}

/**
 * L'evenement joint a un post.
 *
 * Le post ne transporte que son identifiant : la fiche se relit ici. Un
 * evenement supprime depuis la publication ne laisse rien — mieux vaut
 * cela qu'une carte vide qui mene nulle part.
 *
 * D'ou la cle de cache, une par evenement. Le feed, lui, revient intact
 * d'un changement d'onglet ; sans cle, cette relecture-ci repartait de
 * rien a chaque remontage et le bloc surgissait apres coup, seul a
 * clignoter au milieu d'un post deja entierement affiche.
 *
 * # C'est la vraie carte
 *
 * C'etait un resume maison — un titre, trois faits sur fond colore — la
 * ou la meme sortie a partout ailleurs son affiche, sa categorie, et le
 * fait qu'on y est inscrit. Un evenement annonce dans un post donnait
 * donc moins a voir que le meme evenement croise dans son onglet, alors
 * que c'est justement pour l'annoncer qu'on l'y a joint.
 *
 * Le bloc ne garde que l'ecart d'avec le texte et l'arret de la
 * propagation : appuyer sur la sortie mene a sa fiche, pas au fil de
 * commentaires du post qui la porte.
 */
function AttachedEvent({
  eventId,
  onNavigate,
}: {
  eventId: string;
  onNavigate?: (event: { stopPropagation: () => void }) => void;
}) {
  const { data } = useAsync(() => eventsApi.get(eventId), [eventId], `event:${eventId}`);
  if (!data) return null;

  return (
    <span className="post__event" onClick={onNavigate}>
      <EventCard event={data} compact />
    </span>
  );
}

export { AttachedEvent };
